import { useEffect, type ReactElement } from "react";
import { Link, match, RouterProvider, useLocation } from "./lib/router";
import { SessionProvider, useSession } from "./lib/session";
import { ToastProvider } from "./lib/toast";
import { BRAND } from "./brand";
import { Logo } from "./components/Logo";
import Library from "./pages/Library";
import LessonPage from "./pages/LessonPage";
import Studio from "./pages/Studio";
import AdaptationPage from "./pages/AdaptationPage";
import Teach from "./pages/Teach";
import Saved from "./pages/Saved";
import Profiles from "./pages/Profiles";
import HandbookPage from "./pages/HandbookPage";
import Pricing from "./pages/Pricing";
import SignIn from "./pages/SignIn";
import Verify from "./pages/Verify";
import Account from "./pages/Account";

const ROUTES: [string, (p: Record<string, string>) => ReactElement][] = [
  ["/", () => <Library />],
  ["/lesson/:id", (p) => <LessonPage id={p.id} />],
  ["/studio/:id", (p) => <Studio id={p.id} />],
  ["/a/:id", (p) => <AdaptationPage id={p.id} />],
  ["/s/:slug", (p) => <AdaptationPage slug={p.slug} />],
  ["/teach/:kind/:id", (p) => <Teach kind={p.kind} id={p.id} />],
  ["/saved", () => <Saved />],
  ["/profiles", () => <Profiles />],
  ["/handbook", () => <HandbookPage />],
  ["/pricing", () => <Pricing />],
  ["/support", () => <Pricing />],
  ["/signin", () => <SignIn />],
  ["/auth/verify", () => <Verify />],
  ["/account", () => <Account />],
];

function Shell() {
  const { path, hash } = useLocation();
  const { me } = useSession();
  let page: ReactElement | null = null;
  for (const [pattern, render] of ROUTES) {
    const params = match(pattern, path);
    if (params) {
      page = render(params);
      break;
    }
  }
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [hash, path]);

  if (path.startsWith("/teach/")) return <main id="main">{page}</main>;
  const nav = [
    ["/", "Lessons"],
    ["/saved", "Saved"],
    ["/profiles", "Class profiles"],
    ["/handbook", "Principles"],
    ["/pricing", "Pricing"],
  ];
  const current = (to: string) => (to === "/" ? path === "/" || path.startsWith("/lesson") || path.startsWith("/studio") : path.startsWith(to));
  return (
    <>
      <a href="#main" className="skip">Skip to content</a>
      <header className="topbar">
        <Link to="/" className="brand" aria-label={`${BRAND.name}, home`}>
          <Logo size={36} />
          <span className="brand-name">
            <b>{BRAND.mark}</b> <span className="brand-rest">{BRAND.rest}</span>
          </span>
        </Link>
        <nav className="nav" aria-label="Main">
          {nav.map(([to, label]) => (
            <Link key={to} to={to} aria-current={current(to) ? "page" : undefined}>{label}</Link>
          ))}
        </nav>
        <div className="account-slot">
          {me?.user && me.access.state === "trial" && me.access.trialEndsAt && (
            <Link to="/pricing" className="trial-badge">Trial · {Math.max(0, Math.ceil((Date.parse(me.access.trialEndsAt) - Date.now()) / 86400000))}d left</Link>
          )}
          {me?.user && me.access.state === "none" && <Link to="/pricing" className="trial-badge trial-badge-end">Subscribe</Link>}
          {me?.user ? (
            <Link to="/account" className="avatar" title={me.user.email} aria-label="Your account">
              {(me.user.displayName || me.user.email)[0].toUpperCase()}
            </Link>
          ) : (
            <Link to={`/signin?next=${encodeURIComponent(path)}`} className="btn btn-quiet">Sign in</Link>
          )}
        </div>
      </header>
      <main id="main" className="main">
        {page ?? (
          <div className="page narrow empty">
            <h1>That page isn't here</h1>
            <p>The link may be old. <Link to="/">Browse all 50 lessons</Link>.</p>
          </div>
        )}
      </main>
      <footer className="footer">
        <p>
          Lessons from <i>The ESL Teacher's Handbook: 50 Ready-to-Use Secondary Lesson Plans &amp; Classroom Strategies</i> by Melina Wong.
          AI adaptations are drafts for a professional to check before teaching.
        </p>
        <p className="footer-cta">
          <a className="shimmer-link" href={BRAND.handbookUrl} target="_blank" rel="noopener noreferrer">
            GET ACCESS TO THE HANDBOOK<span className="visually-hidden"> (opens Amazon in a new window)</span>
          </a>
        </p>
        <p className="footer-legal">{BRAND.copyright}</p>
        <p className="footer-contact">
          email&nbsp;&nbsp;:&nbsp;&nbsp;
          {BRAND.emails.map((e, i) => (
            <span key={e}>
              {i > 0 && <>&nbsp;&nbsp;·&nbsp;&nbsp;</>}
              <a href={`mailto:${e}`}>{e}</a>
            </span>
          ))}
        </p>
      </footer>
    </>
  );
}

export default function App() {
  return (
    <RouterProvider>
      <ToastProvider>
        <SessionProvider>
          <Shell />
        </SessionProvider>
      </ToastProvider>
    </RouterProvider>
  );
}
